export interface IBillingPaymentSelectionInvoice {
  uuid: string;
  outstanding_amount: number;
  has_pending_payment?: boolean;
  pending_payment_uuid?: string | null;
}

export const getPaymentDialogRows = <T extends IBillingPaymentSelectionInvoice>(
  invoices: T[],
  dialogInvoiceUuids: string[]
): T[] => {
  const selectedUuidSet = new Set(dialogInvoiceUuids);
  return invoices.filter((invoice) => selectedUuidSet.has(invoice.uuid));
};

export const getPaymentDialogOutstandingTotal = (
  invoices: IBillingPaymentSelectionInvoice[],
  dialogInvoiceUuids: string[]
): number =>
  getPaymentDialogRows(invoices, dialogInvoiceUuids).reduce(
    (sum, invoice) => sum + Math.round(Number(invoice.outstanding_amount || 0) * 100),
    0
  ) / 100;

export const isPaymentInvoiceSelectable = (
  invoice: IBillingPaymentSelectionInvoice,
  blockedInvoiceUuidSet: Set<string> = new Set()
): boolean =>
  Number(invoice.outstanding_amount || 0) > 0 && !invoice.has_pending_payment && !invoice.pending_payment_uuid && !blockedInvoiceUuidSet.has(invoice.uuid);

export const getSelectablePaymentInvoices = <T extends IBillingPaymentSelectionInvoice>(
  invoices: T[],
  blockedInvoiceUuidSet: Set<string> = new Set()
): T[] => invoices.filter((invoice) => isPaymentInvoiceSelectable(invoice, blockedInvoiceUuidSet));

export const isBillingPaymentAmountInvalid = (
  isDialogOpen: boolean,
  paymentAmount: number,
  outstandingTotal: number
): boolean =>
  isDialogOpen &&
  (!Number.isFinite(paymentAmount) || paymentAmount <= 0 || paymentAmount > outstandingTotal);

export interface ICustomerPaymentEvidenceDialogState {
  scopedCustomerCode: string;
  billingRoundUuid?: string;
  paymentsRoundKey?: string;
  selectedInvoiceUuids: string[];
  selectedOutstandingTotal: number;
  isLoading?: boolean;
}

export const canOpenCustomerPaymentEvidenceDialog = ({
  scopedCustomerCode,
  billingRoundUuid,
  paymentsRoundKey,
  selectedInvoiceUuids,
  selectedOutstandingTotal,
  isLoading = false,
}: ICustomerPaymentEvidenceDialogState): boolean =>
  !!scopedCustomerCode &&
  !!billingRoundUuid &&
  !!paymentsRoundKey &&
  selectedInvoiceUuids.length > 0 &&
  Number(selectedOutstandingTotal || 0) > 0 &&
  !isLoading;

export interface IBillingPaymentReviewEvidence {
  transfer_amount?: number | string | null;
}

export interface IPaymentReviewEvidenceAmountState {
  hasEvidenceAmount: boolean;
  totalAmount: number;
  delta: number;
  isMismatch: boolean;
}

const toEvidenceAmount = (amount: number | string | null | undefined): number | null => {
  if (amount === undefined || amount === null) return null;
  const parsedAmount = Number(amount);
  return Number.isFinite(parsedAmount) ? parsedAmount : null;
};

const roundMoney = (amount: number): number => Math.round(amount * 100) / 100;

export const getPaymentReviewEvidenceAmountState = (
  evidenceFiles: IBillingPaymentReviewEvidence[],
  paymentAmount: number
): IPaymentReviewEvidenceAmountState => {
  const evidenceAmounts = evidenceFiles
    .map((evidence) => toEvidenceAmount(evidence.transfer_amount))
    .filter((amount): amount is number => amount !== null);
  const totalAmount = roundMoney(evidenceAmounts.reduce((sum, amount) => sum + amount, 0));
  const targetAmount = roundMoney(Number(paymentAmount || 0));
  const delta = roundMoney(Math.abs(totalAmount - targetAmount));

  return {
    hasEvidenceAmount: evidenceAmounts.length > 0,
    totalAmount,
    delta,
    isMismatch: evidenceAmounts.length > 0 && delta >= 0.01,
  };
};

export const shouldBlockPaymentReviewApproval = (
  evidenceFiles: IBillingPaymentReviewEvidence[],
  paymentAmount: number
): boolean => getPaymentReviewEvidenceAmountState(evidenceFiles, paymentAmount).isMismatch;

export type TBillingPaymentSubmissionMethod =
  | "cash"
  | "transfer"
  | "cheque"
  | "credit_card"
  | "other"
  | string;

export const shouldRequirePaymentEvidence = (
  paymentMethod: TBillingPaymentSubmissionMethod
): boolean => ["transfer", "cheque", "credit_card"].includes(paymentMethod);

export const shouldBlockPaymentSubmissionForMissingEvidence = (
  paymentMethod: TBillingPaymentSubmissionMethod,
  evidenceFileCount: number
): boolean => shouldRequirePaymentEvidence(paymentMethod) && evidenceFileCount <= 0;

export interface IBillingCustomerScopeOption {
  customer_code: string;
}

export interface IBillingCustomerRoundOption {
  id?: string | null;
  uuid?: string | null;
  customer_code?: string | null;
}

export const getBillingRoundUuid = (
  round?: Pick<IBillingCustomerRoundOption, "id" | "uuid"> | null
): string => String(round?.id || round?.uuid || "").trim();

export const isBillingCustomerCodeAllowed = (
  customerCode: string,
  customerOptions: IBillingCustomerScopeOption[]
): boolean =>
  !!customerCode && customerOptions.some((customer) => customer.customer_code === customerCode);

export const getScopedBillingCustomerCode = (
  requestedCustomerCode: string,
  customerOptions: IBillingCustomerScopeOption[]
): string => {
  if (customerOptions.length === 0) return "";
  if (isBillingCustomerCodeAllowed(requestedCustomerCode, customerOptions)) {
    return requestedCustomerCode;
  }
  return customerOptions[0].customer_code;
};

export const getScopedCustomerRoundOptions = <T extends IBillingCustomerRoundOption>(
  rounds: T[],
  customerCode: string
): T[] => {
  const scopedCustomerCode = String(customerCode || "").trim();
  if (!scopedCustomerCode) return [];

  const customerRounds = rounds.filter(
    (round) => String(round.customer_code || "").trim() === scopedCustomerCode
  );
  if (customerRounds.length > 0) return customerRounds;

  return rounds.filter((round) => !String(round.customer_code || "").trim());
};
